import { Link } from "react-router-dom";
import { Phone, MessageCircle } from "lucide-react";
import heroPicture from "@/assets/hero-care.jpg?w=640;960;1280;1920&format=avif;webp;jpg&as=picture";

const HeroSection = () => (
  <section className="relative overflow-hidden bg-background">
    <div className="container py-16 sm:py-20 md:py-28 lg:py-36">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center max-w-6xl mx-auto">
        {/* Editorial copy column */}
        <div className="order-2 lg:order-1 animate-fade-in">
          <div className="inline-block mb-7">
            <span className="text-xs sm:text-sm uppercase tracking-[0.2em] text-warm-ink font-semibold">
              Senior Caregivers &amp; In-Home Care · Nashville, TN
            </span>
            <div className="h-px w-12 bg-warm mt-3" aria-hidden="true" />
          </div>
          <h1 className="text-[clamp(2.4rem,6vw,4.5rem)] font-heading font-semibold leading-[1.05] text-primary mb-7 text-balance">
            Senior companion caregivers in Nashville, <br />
            <span className="italic font-normal">rooted in kinship.</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-5 leading-relaxed max-w-xl font-light">
            Non-medical <Link to="/home-care-nashville" className="underline underline-offset-4">in-home care</Link> and <Link to="/elder-care-nashville" className="underline underline-offset-4">elder care</Link> for older adults: one consistent senior caregiver for companionship, rides, meals and daily help, plus <Link to="/respite-care-nashville" className="underline underline-offset-4">respite</Link> for family caregivers, so your loved one can age in place.
          </p>
          <p className="text-base md:text-lg text-muted-foreground mb-9 leading-relaxed max-w-xl font-light">
            Thoughtfully matched senior companion caregivers, chosen to fit your family — not just your calendar. Serving Nashville, Brentwood, Franklin, Belle Meade &amp; Bellevue.
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center gap-5 mb-8">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-10 py-4 bg-warm text-warm-foreground font-semibold tracking-widest text-xs uppercase hover:bg-warm/90 transition-all duration-300 shadow-lg min-h-[44px]"
            >
              Request a Private Consultation
            </Link>
            <Link
              to="/services#how-it-works"
              className="text-primary/80 hover:text-primary underline underline-offset-4 text-base font-light transition-colors"
            >
              or see how it works
            </Link>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <a href="tel:+16158070618" className="inline-flex items-center gap-2 text-primary/80 hover:text-primary transition-colors text-sm">
              <Phone className="h-4 w-4" />
              Call (615) 807-0618
            </a>
            <a href="sms:+16158070618" className="inline-flex items-center gap-2 text-primary/80 hover:text-primary transition-colors text-sm">
              <MessageCircle className="h-4 w-4" />
              Text us — we reply quickly
            </a>
          </div>
          <p className="mt-6 text-xs md:text-sm text-muted-foreground font-light max-w-xl">
            We care exclusively for older adults aging at home — no childcare or foster/kinship placement services.
          </p>
        </div>

        {/* Arch-framed portrait */}
        <div className="order-1 lg:order-2 relative flex justify-center animate-fade-in">
          <div className="w-full max-w-md aspect-[4/5] rounded-t-full border border-warm/30 p-3 bg-white shadow-2xl">
            <picture>
              {Object.entries(heroPicture.sources).map(([type, srcset]) => (
                <source key={type} type={`image/${type}`} srcSet={srcset as string} sizes="(min-width: 1024px) 30rem, 100vw" />
              ))}
              <img
                src={heroPicture.img.src}
                alt="A gracious Nashville home — sunlit sitting room with a companion concierge and an older woman in conversation over tea"
                className="w-full h-full object-cover rounded-t-full"
                width={heroPicture.img.w}
                height={heroPicture.img.h}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                sizes="(min-width: 1024px) 30rem, 100vw"
              />
            </picture>
          </div>
          {/* Corner flourish */}
          <div className="absolute -bottom-4 right-4 lg:-right-4 w-24 h-24 border-b border-r border-warm opacity-40 pointer-events-none" aria-hidden="true" />
        </div>
      </div>
    </div>
  </section>
);

export default HeroSection;
